import CancelSm from '@Resources/assets/icons/dark/cancelSm.svg';
import { CircularProgress } from '@mui/material';
import { SvgIcon } from '@mui/material';
import * as d3 from 'd3';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { IconButton } from '@Components/IconButton';
import { renderEquimentAndWireInfo } from '@Components/InspectEntityComponents/EntityDrawerUtils';
import {
    IProcessedQcImage,
    IQcPole,
    IQcSpanEntity,
    ISvgLabelData,
    QcPhotoPosition,
} from '@Data/qualityControl/Interfaces';
import {
    areImagesLoadingState,
    backPoleState,
    backSpanState,
    centrePoleState,
    forePoleState,
    foreSpanState,
    imageIndicesState,
} from '@Data/qualityControl/Reducer';
import symbology from '@Views/QualityControl/styles/symbology.scss';
import {
    appendSvgDataLabels,
    getHorizontalOffset,
    getImageContainerWidth,
    getImageXCoordinates,
    getNoHeightLabelYValue,
    getOriginalImageBounds,
    getSymbology,
    processPhotosData,
    renderBlankImage,
} from './SVGHelpers';
import Styles from './SVGPoleViewer.scss';
import {
    CAROUSEL_HEIGHT,
    LABEL_DROPDOWN_WIDTH,
    LABEL_HEIGHT,
    LG_LABEL_WIDTH,
    RESIZE_REFRESH_DELAY,
    SM_LABEL_WIDTH,
} from './constants';

const SVGPoleViewer = () => {
    const imageIndices = useSelector(imageIndicesState);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { width, height, ref } = useResizeDetector({
        refreshMode: 'debounce',
        refreshRate: RESIZE_REFRESH_DELAY,
    });
    const imgContainerWidth = getImageContainerWidth(width);
    const svgRef = useRef(null);
    const backPole = useSelector(backPoleState);
    const backSpan = useSelector(backSpanState);
    const centrePole = useSelector(centrePoleState);
    const foreSpan = useSelector(foreSpanState);
    const forePole = useSelector(forePoleState);
    const areImagesLoading = useSelector(areImagesLoadingState);
    const [loadingImg1Index, setLoadingImg1Index] = useState(100);
    const [loadingImg2Index, setLoadingImg2Index] = useState(101);
    const [loadingImg3Index, setLoadingImg3Index] = useState(102);
    const [loadingImg4Index, setLoadingImg4Index] = useState(103);
    const [labelDropdownTitle, setLabelDropdownTitle] = useState<string>('');
    const [labelDropdownData, setLabelDropdownData] = useState<any[]>([]);

    const svgData = useMemo(() => {
        return [backPole, backSpan, centrePole, foreSpan, forePole];
    }, [backPole, backSpan, centrePole, foreSpan, forePole]);

    useEffect(() => {
        setIsLoading(true);
        findIndicesOfLoadingImages(areImagesLoading);
        if (width && height && svgData[2].truesizeImageUrls.length > 0) {
            try {
                closeLabelDropdown();
                buildPoleViewer(svgData);
                setIsLoading(false);
            } catch (e) {
                console.log(e);
            }
        }
    }, [
        width,
        height,
        imageIndices,
        svgData,
        areImagesLoading,
        loadingImg1Index,
        loadingImg2Index,
        loadingImg3Index,
    ]);

    function findIndicesOfLoadingImages(boolArray) {
        let index1 = 100;
        let index2 = 101;
        let index3 = 102;
        let index4 = 103;

        for (let i = 0; i < boolArray.length; i++) {
            if (boolArray[i] === true) {
                if (index1 === 100) {
                    index1 = i;
                } else if (index2 === 101) {
                    index2 = i;
                } else if (index3 === 102) {
                    index3 = i;
                } else {
                    index4 = i;
                    break;
                }
            }
        }
        setLoadingImg1Index(index1);
        setLoadingImg2Index(index2);
        setLoadingImg3Index(index3);
        setLoadingImg4Index(index4);
    }

    const labelDropdown = d3.select('#label-dropdown');

    const resetLabelDropdown = () => {
        setLabelDropdownData([]);
        setLabelDropdownTitle('');
    };

    const closeLabelDropdown = () => {
        resetLabelDropdown();

        labelDropdown
            .transition()
            .duration(300)
            .style('height', '0px')
            .style('opacity', 0);

        return;
    };

    const buildPoleViewer = (photosData: (IQcPole | IQcSpanEntity)[]) => {
        d3.selectAll('#svg-pole-viewer > svg > *').remove();

        const svg = d3.select(svgRef.current);

        const windowWidth = width;
        const viewerHeight = height;

        const imageXCoords = getImageXCoordinates(
            windowWidth,
            imgContainerWidth
        );

        const horizontalOffset = getHorizontalOffset(imgContainerWidth);

        const processedPhotosData = processPhotosData(
            photosData,
            viewerHeight,
            imgContainerWidth
        );

        svg.attr('viewBox', [0, 0, windowWidth, viewerHeight]);

        const renderImagesLabelsAndLines = (
            imageData: IProcessedQcImage,
            index: number
        ) => {
            if (
                index === loadingImg1Index ||
                index === loadingImg2Index ||
                index === loadingImg3Index ||
                index === loadingImg4Index
            ) {
                const loadingImageSpinner = d3
                    .select(`#loadingImg${index}`)
                    .style('left', imageXCoords[index] + 'px')
                    .style('width', imgContainerWidth + 'px')
                    .style('height', viewerHeight + 'px');
            } else if (
                _isNil(imageData.truesizeImageUrls[0])
                // || isNaN(imageData.imageDeltas.imageWidth)
            ) {
                const blankImageText = isNaN(imageData.imageDeltas.imageWidth)
                    ? 'Measurements not available'
                    : 'No image available';
                renderBlankImage(
                    svg,
                    imageXCoords[index],
                    imgContainerWidth,
                    viewerHeight,
                    blankImageText
                );
            } else {
                const clip = svg
                    .append('defs')
                    .append('SVG:clipPath')
                    .attr('id', `clip${imageData.photoPosition}`)
                    .append('SVG:rect')
                    .attr('x', imageXCoords[index])
                    .attr('y', 0)
                    .attr('width', imgContainerWidth)
                    .attr('height', viewerHeight)
                    .style('background', '#f2f2f2');

                const imageContainer = svg
                    .append('g')
                    .attr('clip-path', `url(#clip${imageData.photoPosition})`);

                const image = imageContainer
                    .append('image')
                    .attr(
                        'xlink:href',
                        imageData.truesizeImageUrls[imageIndices[index]]
                    )
                    .attr('x', (d: any) => {
                        if (isNaN(imageData.imageDeltas.imageXCorrection)) {
                            return (
                                imageXCoords[index] - 0.2 * imgContainerWidth
                            );
                        } else {
                            return (
                                imageXCoords[index] -
                                imageData.imageDeltas.imageXCorrection
                            );
                        }
                    })
                    .attr('y', 0)
                    .attr('height', viewerHeight)
                    .style('cursor', 'grab');

                const colouredBorder = imageContainer
                    .append('rect')
                    .attr('class', 'coloured-border')
                    .attr('x', imageXCoords[index])
                    .attr('y', 0)
                    .attr('width', imgContainerWidth)
                    .attr('height', viewerHeight)
                    .attr('fill', 'none')
                    .attr('stroke', (d) => {
                        if (index === 0) {
                            return symbology.backPole;
                        } else if (index === 2) {
                            return symbology.centerPole;
                        } else if (index === 4) {
                            return symbology.forePole;
                        } else {
                            return 'none';
                        }
                    })
                    .attr('stroke-width', 5);

                const clickLabel = (
                    d: ISvgLabelData,
                    e,
                    direction: 'forward' | 'backward',
                    hasHeight = true
                ) => {
                    if (!_isEmpty(d.dropdownData)) {
                        const ikeOfficeData = renderEquimentAndWireInfo(
                            d.dropdownData,
                            d.type
                        );

                        setLabelDropdownTitle(d.name);
                        setLabelDropdownData(ikeOfficeData.dataList);

                        const x =
                            direction === 'backward'
                                ? imageXCoords[index] - LABEL_DROPDOWN_WIDTH
                                : imageXCoords[index] + imgContainerWidth;

                        const y = hasHeight
                            ? d.labelY
                            : getNoHeightLabelYValue(viewerHeight, d.index);

                        labelDropdown
                            .style('left', x + 'px')
                            .style('top', y + 'px')
                            .transition()
                            .duration(300)
                            .style('height', 'auto')
                            .style('opacity', 1);
                    } else {
                        return null;
                    }
                };

                let backLabelLines,
                    backHorizontalLines,
                    forwardLabelLines,
                    forwardHorizontalLines,
                    outerConnectorLines;

                const dataLabelDimensions = {
                    imgContainerWidth: imgContainerWidth,
                    viewerHeight: viewerHeight,
                };

                // if the image has attachments
                if (imageIndices[index] === 0) {
                    // back labels
                    appendSvgDataLabels(
                        svg,
                        imageData.backwardLabels,
                        `.backGroup${imageData.photoPosition}`,
                        (e, d) => {
                            clickLabel(d, e, 'backward');
                        },
                        imageData.photoPosition,
                        imageXCoords[index],
                        true,
                        'back',
                        dataLabelDimensions
                    );

                    // forward labels
                    appendSvgDataLabels(
                        svg,
                        imageData.forwardLabels,
                        `.forwardGroup${imageData.photoPosition}`,
                        (e, d) => {
                            clickLabel(d, e, 'forward');
                        },
                        imageData.photoPosition,
                        imageXCoords[index],
                        true,
                        'fore',
                        dataLabelDimensions
                    );

                    if (
                        imageData.photoPosition === QcPhotoPosition.CENTREPOLE
                    ) {
                        // back no height labels
                        appendSvgDataLabels(
                            svg,
                            imageData.backwardNoHeightLabels,
                            `.backNoHeightGroup${imageData.photoPosition}`,
                            (e, d) => {
                                clickLabel(d, e, 'backward', false);
                            },
                            QcPhotoPosition.CENTREPOLE,
                            imageXCoords[index],
                            false,
                            'back',
                            dataLabelDimensions
                        );

                        // forward no height labels
                        appendSvgDataLabels(
                            svg,
                            imageData.forwardNoHeightLabels,
                            `.foreNoHeightGroup${imageData.photoPosition}`,
                            (e, d) => {
                                clickLabel(d, e, 'forward', false);
                            },
                            QcPhotoPosition.CENTREPOLE,
                            imageXCoords[index],
                            false,
                            'fore',
                            dataLabelDimensions
                        );
                    }

                    backLabelLines = svg
                        .selectAll(`.backLabelLines${imageData.photoPosition}`)
                        .data(imageData.backwardLabels)
                        .join('line')
                        .attr(
                            'class',
                            `backLabelLines${imageData.photoPosition}`
                        )
                        .attr('x1', imageXCoords[index])
                        .attr('y1', (d: any) => d.labelY + LABEL_HEIGHT / 2)
                        .attr(
                            'x2',
                            (d: any) =>
                                imageXCoords[index] + d.x - horizontalOffset
                        )
                        .attr('y2', (d: any) => d.y)
                        .attr('stroke', (d) =>
                            getSymbology(d, 'back', imageData.photoPosition)
                        )
                        .attr('stroke-width', 1);

                    backHorizontalLines = svg
                        .selectAll(
                            `.backHorizonalLines${imageData.photoPosition}`
                        )
                        .data(imageData.backwardLabels)
                        .join('line')
                        .attr(
                            'class',
                            `backHorizonalLines${imageData.photoPosition}`
                        )
                        .attr(
                            'x1',
                            (d: any) =>
                                imageXCoords[index] + d.x - horizontalOffset
                        )
                        .attr('y1', (d: any) => d.y)
                        .attr('x2', (d: any) => imageXCoords[index] + d.x)
                        .attr('y2', (d: any) => d.y)
                        .attr('stroke', (d) =>
                            getSymbology(d, 'back', imageData.photoPosition)
                        )
                        .attr('stroke-width', 1);

                    forwardLabelLines = svg
                        .selectAll(
                            `.forwardLabelLines${imageData.photoPosition}`
                        )
                        .data(imageData.forwardLabels)
                        .join('line')
                        .attr(
                            'class',
                            `forwardLabelLines${imageData.photoPosition}`
                        )
                        .attr('x1', imageXCoords[index] + imgContainerWidth)
                        .attr('y1', (d: any) => d.labelY + LABEL_HEIGHT / 2)
                        .attr(
                            'x2',
                            (d: any) =>
                                imageXCoords[index] + d.x + horizontalOffset
                        )
                        .attr('y2', (d: any) => d.y)
                        .attr('stroke', (d) =>
                            getSymbology(d, 'fore', imageData.photoPosition)
                        )
                        .attr('stroke-width', 1);

                    forwardHorizontalLines = svg
                        .selectAll(
                            `.forwardHorizontalLines${imageData.photoPosition}`
                        )
                        .data(imageData.forwardLabels)
                        .join('line')
                        .attr(
                            'class',
                            `forwardHorizontalLines${imageData.photoPosition}`
                        )
                        .attr('x1', (d: any) => imageXCoords[index] + d.x)
                        .attr('y1', (d: any) => d.y)
                        .attr(
                            'x2',
                            (d: any) =>
                                imageXCoords[index] + d.x + horizontalOffset
                        )
                        .attr('y2', (d: any) => d.y)
                        .attr('stroke', (d) =>
                            getSymbology(d, 'fore', imageData.photoPosition)
                        )
                        .attr('stroke-width', 1);

                    if (
                        imageData.photoPosition === QcPhotoPosition.BACKSPAN ||
                        imageData.photoPosition === QcPhotoPosition.FORESPAN
                    ) {
                        const type =
                            imageData.photoPosition === QcPhotoPosition.BACKSPAN
                                ? 'b'
                                : 'f';
                        const centreConnectorLines = svg
                            .selectAll(
                                `.centreConnectorLines${imageData.photoPosition}`
                            )
                            .data(
                                type === 'b'
                                    ? imageData.forwardLabels
                                    : imageData.backwardLabels
                            )
                            .join('line')
                            .filter((d: any) => {
                                if (type === 'b') {
                                    const connectionExistsInCentreBwdLabels =
                                        processedPhotosData[2].backwardLabels.find(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        ) !== undefined;
                                    return connectionExistsInCentreBwdLabels;
                                } else {
                                    const connectionExistsInCentreFwdLabels =
                                        processedPhotosData[2].forwardLabels.find(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        ) !== undefined;
                                    return connectionExistsInCentreFwdLabels;
                                }
                            })
                            .attr(
                                'class',
                                `centreConnectorLines${imageData.photoPosition}`
                            )
                            .attr('x1', (d: any) => {
                                if (type === 'b') {
                                    const rightEdgeSmLabel =
                                        imageXCoords[index] +
                                        imgContainerWidth +
                                        SM_LABEL_WIDTH;
                                    return rightEdgeSmLabel;
                                } else {
                                    const leftEdgeSmLabel =
                                        imageXCoords[index] - SM_LABEL_WIDTH;
                                    return leftEdgeSmLabel;
                                }
                            })
                            .attr('y1', (d: any) => d.y)
                            .attr('x2', (d: any) => {
                                if (type === 'b') {
                                    const rightEdgeLgLabel =
                                        imageXCoords[2] - LG_LABEL_WIDTH;
                                    return rightEdgeLgLabel;
                                } else {
                                    const leftEdgeLgLabel =
                                        imageXCoords[2] +
                                        imgContainerWidth +
                                        LG_LABEL_WIDTH;
                                    return leftEdgeLgLabel;
                                }
                            })
                            .attr('y2', (d: any) => {
                                if (type === 'b') {
                                    const labelIndex =
                                        processedPhotosData[2].backwardLabels.findIndex(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        );
                                    const connectionYOnCentreBwdLabel =
                                        processedPhotosData[2].backwardLabels[
                                            labelIndex
                                        ].labelY +
                                        LABEL_HEIGHT / 2;
                                    return connectionYOnCentreBwdLabel;
                                } else {
                                    const labelIndex =
                                        processedPhotosData[2].forwardLabels.findIndex(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        );
                                    const connectionYOnCentreFwdLabel =
                                        processedPhotosData[2].forwardLabels[
                                            labelIndex
                                        ].labelY +
                                        LABEL_HEIGHT / 2;
                                    return connectionYOnCentreFwdLabel;
                                }
                            })
                            .attr(
                                'stroke',
                                type === 'b'
                                    ? symbology.backPole
                                    : symbology.forePole
                            )
                            .attr('stroke-width', 1);

                        outerConnectorLines = svg
                            .selectAll(
                                `.outerConnectorLines${imageData.photoPosition}`
                            )
                            .data(
                                type === 'b'
                                    ? imageData.forwardLabels
                                    : imageData.backwardLabels
                            )
                            .join('line')
                            .filter((d: any) => {
                                if (type === 'b') {
                                    const connectionExistsInBackPoleFwdLabels =
                                        processedPhotosData[0].forwardLabels.find(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        ) !== undefined;
                                    return connectionExistsInBackPoleFwdLabels;
                                } else {
                                    const connectionExistsInFwdPoleBwdLabels =
                                        processedPhotosData[4].backwardLabels.find(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        ) !== undefined;
                                    return connectionExistsInFwdPoleBwdLabels;
                                }
                            })
                            .attr(
                                'class',
                                `outerConnectorLines${imageData.photoPosition}`
                            )
                            .attr('x1', (d: any) => {
                                if (type === 'b') {
                                    const rightEdgeBackPoleLabelX =
                                        imageXCoords[0] +
                                        imgContainerWidth +
                                        SM_LABEL_WIDTH;
                                    return rightEdgeBackPoleLabelX;
                                } else {
                                    const leftEdgeFwdPoleLabelX =
                                        imageXCoords[4] - SM_LABEL_WIDTH;
                                    return leftEdgeFwdPoleLabelX;
                                }
                            })
                            .attr('y1', (d: any) => {
                                if (type === 'b') {
                                    const labelIndex =
                                        processedPhotosData[0].forwardLabels.findIndex(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        );
                                    const connectionYOnBackPoleFwdLabel =
                                        processedPhotosData[0].forwardLabels[
                                            labelIndex
                                        ].labelY +
                                        LABEL_HEIGHT / 2;
                                    return connectionYOnBackPoleFwdLabel;
                                } else {
                                    const labelIndex =
                                        processedPhotosData[4].backwardLabels.findIndex(
                                            (label: any) => {
                                                return label.id === d.id;
                                            }
                                        );
                                    const connectionYOnFwdPoleBwdLabel =
                                        processedPhotosData[4].backwardLabels[
                                            labelIndex
                                        ].labelY +
                                        LABEL_HEIGHT / 2;
                                    return connectionYOnFwdPoleBwdLabel;
                                }
                            })
                            .attr('x2', (d: any) => imageXCoords[index] + d.x)
                            .attr('y2', (d: any) => d.y)
                            .attr(
                                'stroke',
                                type === 'b'
                                    ? symbology.backPole
                                    : symbology.forePole
                            )
                            .attr('stroke-width', 1);
                    }
                }

                const origImageBounds = getOriginalImageBounds(
                    imageXCoords[index],
                    processedPhotosData[index].imageDeltas.imageXCorrection,
                    processedPhotosData[index].imageDeltas.imageWidth,
                    viewerHeight
                );

                const zoom = d3
                    .zoom()
                    .extent([
                        [origImageBounds.x1, origImageBounds.y1],
                        [origImageBounds.x2, origImageBounds.y2],
                    ])
                    .scaleExtent([1, 10])
                    .translateExtent([
                        [origImageBounds.x1, origImageBounds.y1],
                        [origImageBounds.x2, origImageBounds.y2],
                    ])
                    .on('zoom', (event: any) => {
                        const transform = event.transform;

                        image.attr('transform', transform);

                        forwardLabelLines
                            .attr('x2', (d: any) => {
                                const origX2 =
                                    imageXCoords[index] +
                                    d.x +
                                    horizontalOffset;
                                let newX2 = origX2 * transform.k + transform.x;

                                if (
                                    newX2 < imageXCoords[index] ||
                                    newX2 >
                                        imageXCoords[index] + imgContainerWidth
                                ) {
                                    newX2 =
                                        (imageXCoords[index] + d.x) *
                                            transform.k +
                                        transform.x;
                                }
                                return newX2;
                            })
                            .attr('y2', (d: any) => {
                                const origY2 = d.y;
                                const newY2 =
                                    origY2 * transform.k + transform.y;
                                return newY2;
                            })
                            .attr('visibility', (d: any, i, e: any) => {
                                if (
                                    e[i].attributes.x2.value <
                                        imageXCoords[index] ||
                                    e[i].attributes.x2.value >
                                        imageXCoords[index] +
                                            imgContainerWidth ||
                                    e[i].attributes.y2.value < 0 ||
                                    e[i].attributes.y2.value > viewerHeight
                                ) {
                                    return 'hidden';
                                } else {
                                    return 'visible';
                                }
                            });

                        forwardHorizontalLines
                            .attr('x1', (d: any) => {
                                const origX1 = imageXCoords[index] + d.x;
                                const newX1 =
                                    origX1 * transform.k + transform.x;

                                return newX1 < imageXCoords[index]
                                    ? imageXCoords[index]
                                    : newX1;
                            })
                            .attr('x2', (d: any) => {
                                const origX2 =
                                    imageXCoords[index] +
                                    d.x +
                                    horizontalOffset;
                                return origX2 * transform.k + transform.x;
                            })
                            .attr('y1', (d: any) => {
                                const origY2 = d.y;
                                const newY2 =
                                    origY2 * transform.k + transform.y;
                                return newY2;
                            })
                            .attr('y2', (d: any) => {
                                const origY2 = d.y;
                                const newY2 =
                                    origY2 * transform.k + transform.y;
                                return newY2;
                            })
                            .attr('visibility', (d: any, i, e: any) => {
                                if (
                                    e[i].attributes.x2.value <
                                        imageXCoords[index] ||
                                    e[i].attributes.x2.value >
                                        imageXCoords[index] +
                                            imgContainerWidth ||
                                    e[i].attributes.y2.value < 0 ||
                                    e[i].attributes.y2.value > viewerHeight
                                ) {
                                    return 'hidden';
                                } else {
                                    return 'visible';
                                }
                            });

                        backLabelLines
                            .attr('x2', (d: any) => {
                                const origX2 =
                                    imageXCoords[index] +
                                    d.x -
                                    horizontalOffset;
                                let newX2 = origX2 * transform.k + transform.x;

                                if (
                                    newX2 < imageXCoords[index] ||
                                    newX2 >
                                        imageXCoords[index] + imgContainerWidth
                                ) {
                                    newX2 =
                                        (imageXCoords[index] + d.x) *
                                            transform.k +
                                        transform.x;
                                }
                                return newX2;
                            })
                            .attr('y2', (d: any) => {
                                const origY2 = d.y;
                                const newY2 =
                                    origY2 * transform.k + transform.y;
                                return newY2;
                            })
                            .attr('visibility', (d: any, i, e: any) => {
                                if (
                                    e[i].attributes.x2.value <
                                        imageXCoords[index] ||
                                    e[i].attributes.x2.value >
                                        imageXCoords[index] +
                                            imgContainerWidth ||
                                    e[i].attributes.y2.value < 0 ||
                                    e[i].attributes.y2.value > viewerHeight
                                ) {
                                    return 'hidden';
                                } else {
                                    return 'visible';
                                }
                            });

                        backHorizontalLines
                            .attr('x1', (d: any) => {
                                const origX1 = imageXCoords[index] + d.x;
                                const newX1 =
                                    origX1 * transform.k + transform.x;

                                const imageEdge =
                                    imageXCoords[index] + imgContainerWidth;
                                return newX1 < imageEdge ? newX1 : imageEdge;
                            })
                            .attr('x2', (d: any) => {
                                const origX2 =
                                    imageXCoords[index] +
                                    d.x -
                                    horizontalOffset;
                                return origX2 * transform.k + transform.x;
                            })
                            .attr('y1', (d: any) => {
                                const origY2 = d.y;
                                const newY2 =
                                    origY2 * transform.k + transform.y;
                                return newY2;
                            })
                            .attr('y2', (d: any) => {
                                const origY2 = d.y;
                                const newY2 =
                                    origY2 * transform.k + transform.y;
                                return newY2;
                            })
                            .attr('visibility', (d: any, i, e: any) => {
                                if (
                                    e[i].attributes.x2.value <
                                        imageXCoords[index] ||
                                    e[i].attributes.x2.value >
                                        imageXCoords[index] +
                                            imgContainerWidth ||
                                    e[i].attributes.y2.value < 0 ||
                                    e[i].attributes.y2.value > viewerHeight
                                ) {
                                    return 'hidden';
                                } else {
                                    return 'visible';
                                }
                            });

                        if (
                            imageData.photoPosition ===
                                QcPhotoPosition.BACKSPAN ||
                            imageData.photoPosition === QcPhotoPosition.FORESPAN
                        ) {
                            outerConnectorLines
                                .attr('x2', (d: any) => {
                                    const origX2 = imageXCoords[index] + d.x;
                                    const newX2 =
                                        origX2 * transform.k + transform.x;
                                    return newX2;
                                })
                                .attr('y2', (d: any) => {
                                    const origY2 = d.y;
                                    const newY2 =
                                        origY2 * transform.k + transform.y;
                                    return newY2;
                                })
                                .attr('visibility', (d: any, i, e: any) => {
                                    if (
                                        e[i].attributes.x2.value <
                                            imageXCoords[index] ||
                                        e[i].attributes.x2.value >
                                            imageXCoords[index] +
                                                imgContainerWidth ||
                                        e[i].attributes.y2.value < 0 ||
                                        e[i].attributes.y2.value > viewerHeight
                                    ) {
                                        return 'hidden';
                                    } else {
                                        return 'visible';
                                    }
                                });
                        }
                    });
                imageContainer.call(zoom);
            }
        };

        processedPhotosData.forEach((photoObject, index) => {
            renderImagesLabelsAndLines(photoObject, index);
        });
    };

    const logError = (error: Error, info: { componentStack: string }) => {
        // Do something with the error, e.g. log to an external API
        console.log('%s %n %s', error, JSON.stringify(info));
    };
    function Fallback({ error, resetErrorBoundary }) {
        // Call resetErrorBoundary() to reset the error boundary and retry the render.

        return (
            <div role="alert">
                <p>Something went wrong:</p>
                <pre style={{ color: 'red' }}>{error.message}</pre>
            </div>
        );
    }

    return (
        <ErrorBoundary FallbackComponent={Fallback} onError={logError}>
            <div
                id="svg-pole-viewer"
                className={Styles.svgWrapper}
                style={{ height: `calc(100% - ${CAROUSEL_HEIGHT}px)` }}
                ref={ref}
            >
                {isLoading && <CircularProgress />}

                <div
                    id="label-dropdown"
                    className={Styles.labelDropdown}
                    style={{ width: `${LABEL_DROPDOWN_WIDTH}px` }}
                >
                    <div
                        className={Styles.labelDropdownTitleRow}
                        onClick={closeLabelDropdown}
                    >
                        <span
                            className={Styles.labelDropdownTitle}
                            title={labelDropdownTitle}
                        >
                            {labelDropdownTitle}
                        </span>
                        <IconButton
                            onClick={closeLabelDropdown}
                            className={Styles.labelDropdownCloseBtn}
                            icon={
                                <SvgIcon>
                                    <CancelSm />
                                </SvgIcon>
                            }
                        />
                    </div>
                    {labelDropdownData.map((d) => {
                        return d as JSX.Element;
                    })}
                </div>
                <div
                    id={`loadingImg${loadingImg1Index}`}
                    className={Styles.loadingImg}
                    style={
                        loadingImg1Index !== 100
                            ? { display: 'flex' }
                            : { display: 'none' }
                    }
                >
                    <CircularProgress />
                </div>
                <div
                    id={`loadingImg${loadingImg2Index}`}
                    className={Styles.loadingImg}
                    style={
                        loadingImg2Index !== 101
                            ? { display: 'flex' }
                            : { display: 'none' }
                    }
                >
                    <CircularProgress />
                </div>
                <div
                    id={`loadingImg${loadingImg3Index}`}
                    className={Styles.loadingImg}
                    style={
                        loadingImg3Index !== 102
                            ? { display: 'flex' }
                            : { display: 'none' }
                    }
                >
                    <CircularProgress />
                </div>
                <div
                    id={`loadingImg${loadingImg4Index}`}
                    className={Styles.loadingImg}
                    style={
                        loadingImg4Index !== 103
                            ? { display: 'flex' }
                            : { display: 'none' }
                    }
                >
                    <CircularProgress />
                </div>
                <svg
                    ref={svgRef}
                    style={isLoading ? { width: '0px' } : { width: '100%' }}
                ></svg>
            </div>
        </ErrorBoundary>
    );
};

export default SVGPoleViewer;
